import { callAI } from './aiService';
import { scoringService, DetailedScore } from './scoringService';
import { templateService } from './templateService';
import { selectDiverseTop, analyzeTypeDistribution, analyzeEndingDuplication } from './diversityService';
import { feedbackService } from './feedbackService';
import {
  TweetSettingsValues,
  describeSettings,
  loadTweetSettingsFromStorage,
} from '../lib/tweetSettings';
import type { Locale } from '../lib/i18n';

function getLocale(): Locale {
  const saved = localStorage.getItem('bangerfy_locale');
  return saved === 'ja' ? 'ja' : 'en';
}

export interface TweetCandidate {
  text: string;
  score: number;
  breakdown: DetailedScore;
  templateType?: string;  // 使用した型（オプション）
}

export interface GenerateTweetsResponse {
  success: boolean;
  candidates: TweetCandidate[];
  message: string;
}

export interface StrategyGenerationResult {
  thoughtLog: string;
  tweet: string;
  teaserSet: {
    teaser: string;
    answer: string;
  };
  thread: string[];
  notePromo: {
    patternA: string;
    patternB: string;
    patternC: string;
  };
}

export interface GenerateStrategyResponse {
  success: boolean;
  strategy: StrategyGenerationResult;
}

export const generateService = {
  /**
   * ツイート生成（2モード）
   * - useTemplates=true: 型×変数で生成
   * - useTemplates=false: 従来の自由形式
   * @param count 生成するツイート数（5〜30）
   * @param returnAll すべての候補を返すか（true: 全部、false: Top5のみ）
   */
  async generateTweets(
    inputText: string,
    stylePrompt: string,
    buzzInsights: string[],
    useTemplates: boolean = true,
    count: number = 20,
    returnAll: boolean = false
  ): Promise<GenerateTweetsResponse> {
    try {
      // Few-shot examples: 実際のユーザーツイートのみ（AI生成物は含めない）
      const styleModelStr = localStorage.getItem('style_model');
      let actualTweets: string[] = [];
      if (styleModelStr) {
        try {
          const styleModel = JSON.parse(styleModelStr);
          actualTweets = styleModel.exampleTweets || [];
        } catch (e) {
          // ignore
        }
      }
      const fewShotExamples = actualTweets.slice(0, 10);

      // フィードバック分析: ユーザーの実績からパターンヒントを取得
      const feedbackInsights = feedbackService.analyze();
      const feedbackHints = feedbackInsights.promptHints;

      // フィードバックがあれば重みも自動調整
      if (feedbackInsights.bangedCount >= 3) {
        feedbackService.applyWeightAdjustments();
      }

      let generationPrompt: string;
      let systemPrompt: string;

      if (useTemplates) {
        // 型×変数モード
        generationPrompt = templateService.buildTemplatePrompt(
          inputText,
          stylePrompt,
          fewShotExamples,
          buzzInsights,
          count
        );
        
        systemPrompt = getLocale() === 'ja'
          ? `あなたは、ユーザーの文体を完璧に再現し、指定された型に沿ってツイートを生成する専門家です。

**生成前の内省（出力しない）:**
- フック: 型に合わせつつ1行目の角度を変える（疑問/結論/体験/比較などを散らす）
- 具体: 固有名詞・数字・体験シーンを必ず1つ以上入れる
- リズム: 改行は0〜2回で読みやすさ優先、絵文字は0〜2個のみ（感情の強調時だけ）
- 末尾: 同じ終わり方を続けない。HOOK_VARIATIONSから被りを避ける

**絶対に守るルール:**
- 文体指示を1ミリも逸脱しない（語尾・句読点・絵文字位置まで再現）
- 280文字を1文字でも超えない
- 各型の中で5通りの異なる切り口を生成（構成とフックを変える）
- 広告・定型文・AIっぽさを一切出さない。体温のある語彙に置き換える

あなたの仕事は「本人が書いたとしか思えないツイート」を型に沿って生成することです。`
          : `You are an expert at perfectly replicating the user's writing style and generating tweets that follow specified templates.

**Pre-generation reflection (do NOT output):**
- Hook: Vary the angle of the opening line for each template (question/conclusion/experience/comparison)
- Specifics: Always include at least one proper noun, number, or concrete moment
- Rhythm: 0-2 line breaks for readability, 0-2 emojis only (for emotional emphasis)
- Endings: Never repeat the same ending pattern. Rotate hooks to avoid repetition

**Absolute rules:**
- Never deviate from the style instructions (replicate tone, sentence endings, emoji placement, punctuation exactly)
- Never exceed 280 characters
- Generate 5 different angles within each template (vary structure and hooks)
- Zero ad-copy, boilerplate, or AI-sounding language. Use warm, human vocabulary

Your job is to generate tweets that read as if the user wrote them personally, following each template.`;
      } else {
        // 従来の自由形式モード
        generationPrompt = this.buildFreeformPrompt(
          inputText,
          stylePrompt,
          fewShotExamples,
          buzzInsights,
          count
        );

        systemPrompt = getLocale() === 'ja'
          ? `あなたは、ユーザーの文体を完璧に再現する文章生成の専門家です。

**生成前の内省（出力しない）:**
- フック: 1行目の驚き/問い/結論/体験/比較をローテーション
- 具体: 固有名詞・数字・体験の瞬間を必ず1つ入れる
- リズム: 改行0〜2回、絵文字0〜2個。過剰強調や「!!」「??」連打は禁止
- 末尾: 疑問/共感/結論/行動喚起を分散させ、連続しない

**絶対に守るルール:**
- 文体指示を1ミリも逸脱しない（語尾・句読点・絵文字位置まで再現）
- 280文字を1文字でも超えない
- 同じ表現・構成の使い回しをしない（20個すべて異なる切り口）
- 広告・定型文・AIっぽさを一切出さない。会話調で揺らぎを残す

あなたの仕事は「本人が書いたとしか思えないツイート」を生成することです。`
          : `You are an expert at perfectly replicating the user's writing style.

**Pre-generation reflection (do NOT output):**
- Hook: Rotate opening angles — surprise/question/conclusion/experience/comparison
- Specifics: Always include at least one proper noun, number, or concrete moment
- Rhythm: 0-2 line breaks, 0-2 emojis. No excessive emphasis like "!!" or "??" spam
- Endings: Distribute question/empathy/conclusion/CTA endings — never repeat consecutively

**Absolute rules:**
- Never deviate from style instructions (replicate tone, endings, emoji placement, punctuation exactly)
- Never exceed 280 characters
- No reuse of the same expressions or structures (all 20 must have different angles)
- Zero ad-copy, boilerplate, or AI-sounding language. Keep it conversational with natural variation

Your job is to generate tweets that read as if the user wrote them personally.`;
      }

      // フィードバックヒントをシステムプロンプトに追加
      if (feedbackHints.length > 0) {
        const hintsBlock = getLocale() === 'ja'
          ? `\n\n**ユーザーの実績データ（これに寄せて生成せよ）:**\n${feedbackHints.map(h => `- ${h}`).join('\n')}`
          : `\n\n**User performance data (lean into these patterns):**\n${feedbackHints.map(h => `- ${h}`).join('\n')}`;
        systemPrompt += hintsBlock;
      }

      const jsonText = await callAI(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: generationPrompt }
        ],
        { temperature: 0.88, maxTokens: 3000, jsonMode: true }
      );

      const json = JSON.parse(jsonText);
      const tweetsArray = json.tweets || [];

      if (tweetsArray.length === 0) {
        throw new Error(getLocale() === 'ja' ? 'ツイート案が生成されませんでした。もう一度試してください。' : 'No tweet drafts were generated. Please try again.');
      }

      const candidates: TweetCandidate[] = [];

      // 重みを取得
      const weights = scoringService.loadWeights();

      for (const item of tweetsArray) {
        const text = typeof item === 'string' ? item : item.text;
        const templateType = typeof item === 'object' ? item.type : undefined;
        
        if (!text || typeof text !== 'string' || !text.trim()) continue;
        
        // 280文字チェック
        if (text.length > 280) continue;

        // 6軸スコアリング
        const detailedScore = scoringService.calculateDetailedScore(
          text,
          stylePrompt,
          fewShotExamples,
          weights
        );

        candidates.push({
          text,
          score: detailedScore.total,
          breakdown: detailedScore,
          templateType,
        });
      }

      if (candidates.length === 0) {
        throw new Error(getLocale() === 'ja' ? '有効なツイート案が生成されませんでした。入力内容を見直してください。' : 'No valid tweet drafts were generated. Please review your input.');
      }

      // スコア順にソート
      const sortedCandidates = candidates.sort((a, b) => b.score - a.score);

      // 返す候補を決定
      const finalCandidates = returnAll ? sortedCandidates : selectDiverseTop(sortedCandidates, 5);

      // デバッグ: 型の分布と末尾重複をチェック（Top5のみ）
      if (process.env.NODE_ENV === 'development' && !returnAll) {
        const typeAnalysis = analyzeTypeDistribution(finalCandidates);
        const endingAnalysis = analyzeEndingDuplication(finalCandidates);
        
        if (typeAnalysis.warnings.length > 0) {
          console.warn('[generateService] 型の偏り:', typeAnalysis.warnings);
        }
        if (endingAnalysis.warnings.length > 0) {
          console.warn('[generateService] 末尾重複:', endingAnalysis.warnings);
        }
      }

      return {
        success: true,
        candidates: finalCandidates,
        message: getLocale() === 'ja' ? `ツイート生成が完了しました（${finalCandidates.length}件）` : `Generated ${finalCandidates.length} tweet drafts`
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Unauthorized')) {
          throw new Error(getLocale() === 'ja' ? '認証トークンが無効です。セットアップをやり直してください。' : 'Invalid authentication token. Please redo setup.');
        }
        throw error;
      }
      throw new Error(getLocale() === 'ja' ? 'ツイート生成に失敗しました' : 'Tweet generation failed');
    }
  },

  /**
   * 戦略セット生成（通常＋ティザー＋note訴求＋ショートスレッド）
   */
  async generateStrategyTweets(
    inputText: string,
    stylePrompt: string,
    settings?: TweetSettingsValues,
    buzzInsights: string[] = []
  ): Promise<GenerateStrategyResponse> {
    try {
      const resolvedSettings = settings || loadTweetSettingsFromStorage();
      const { length, tone, style } = describeSettings(resolvedSettings);
      const humanizeLevel = resolvedSettings?.humanizeLevel ?? 3;

      // Few-shot examplesを取得（styleModelから実際のツイート例）
      const styleModelStr = localStorage.getItem('style_model');
      let actualTweets: string[] = [];
      if (styleModelStr) {
        try {
          const styleModel = JSON.parse(styleModelStr);
          actualTweets = styleModel.exampleTweets || [];
        } catch (e) {
          // ignore
        }
      }

      // 過去の高評価生成ツイート（補助的）
      const savedTweets = (() => { try { return JSON.parse(localStorage.getItem('bangerfy_history') || '[]'); } catch { return []; } })();
      const highScoreTweets = savedTweets
        .filter((item: any) => item.score && item.score > 0.75)
        .slice(0, 3)
        .map((item: any) => item.content);

      const fewShotExamples = [...actualTweets.slice(0, 7), ...highScoreTweets].slice(0, 10);
      const locale = getLocale();

      const fewShotSection = fewShotExamples.length > 0
        ? locale === 'ja'
          ? `\n### 参考スタイル（このユーザーの実ツイート）\n${fewShotExamples.map((t, i) => `${i + 1}. ${t}`).join('\n')}`
          : `\n### Reference style (this user's actual tweets)\n${fewShotExamples.map((t, i) => `${i + 1}. ${t}`).join('\n')}`
        : '';

      const buzzSection = buzzInsights.length > 0
        ? locale === 'ja'
          ? `\n### バズ示唆（任意で反映）：\n${buzzInsights.slice(0, 5).map((i, idx) => `${idx + 1}. ${i}`).join('\n')}`
          : `\n### Viral insights (apply where relevant):\n${buzzInsights.slice(0, 5).map((i, idx) => `${idx + 1}. ${i}`).join('\n')}`
        : '';

      const constraintText = locale === 'ja'
        ? `長さ=${length} / トーン=${tone} / スタイル=${style} / AI臭さ除去=Lv${humanizeLevel}`
        : `Length=${length} / Tone=${tone} / Style=${style} / Anti-AI=Lv${humanizeLevel}`;

      const userPrompt = locale === 'ja'
        ? `あなたはX（Twitter）運用のトップコンサルタントです。以下の7ステップで思考し、JSONのみで出力してください。\n\n### 入力\nテーマ: ${inputText}\n文体: ${stylePrompt || '（文体プロンプト未設定の場合は口語ベースで）'}\n制約: ${constraintText}${buzzSection}${fewShotSection}\n\n### 思考プロセス（Chain of Thought）\n1. 【意外性】このテーマで誰も言っていない切り口は？\n2. 【0.3秒テスト】スクロールを止める初動1行は？\n3. 【フック選定】v3.0の80フックから最適解を選ぶ理由は？\n4. 【骨格】結論→理由→オチの論理構成（3〜5行）を設計\n5. 【AI排除】Lv${humanizeLevel}に基づき、定型表現や語尾をどう崩すか？\n6. 【イメージ】読後に読者はどう動くか？\n7. 【完成】各フォーマットを280文字以内で出力\n\n### 出力形式（JSONのみ）\n{\n  "thought_log": "ステップ1〜6の要約を箇条書きまたは短文で。最大6行。",\n  "tweet": "通常ツイート。280文字以内。",\n  "teaser_set": { "teaser": "投稿1: フックだけ", "answer": "投稿2: 解答・3行以内" },\n  "thread": ["1/3 ...", "2/3 ...", "3/3 ..."],\n  "note_promo": { "pattern_a": "ストレート訴求", "pattern_b": "しれっと訴求", "pattern_c": "バズ便乗訴求" }\n}\n\n### 厳守ルール\n- JSONオブジェクト以外の文字を一切出力しない（コードブロック禁止）\n- すべて日本語。各行は自然な口語でAI臭さを避ける\n- teaser_set/answer・thread・note_promoも280文字以内\n- threadは3連投（MVP）。1/3, 2/3, 3/3 で始める\n- teaserと通常ツイートで切り口を微妙に変える（コピペ禁止）\n- note訴求はnote記事誘導に直結する具体表現を入れる\n- thought_log以外に思考過程を書かない\n`
        : `You are a top X (Twitter) growth consultant. Think through the 7 steps below and output ONLY JSON.\n\n### Input\nTopic: ${inputText}\nStyle: ${stylePrompt || '(No style prompt set — use casual conversational tone)'}\nConstraints: ${constraintText}${buzzSection}${fewShotSection}\n\n### Thinking process (Chain of Thought)\n1. [Surprise] What angle has nobody taken on this topic?\n2. [0.3s test] What opening line stops the scroll?\n3. [Hook selection] Which hook pattern works best and why?\n4. [Structure] Design the logic flow: conclusion → reason → punchline (3-5 lines)\n5. [Anti-AI] Based on Lv${humanizeLevel}, how to break up boilerplate and vary endings?\n6. [Vision] What will the reader do after reading?\n7. [Final] Output each format within 280 characters\n\n### Output format (JSON only)\n{\n  "thought_log": "Summary of steps 1-6 in bullet points or short sentences. Max 6 lines.",\n  "tweet": "Standard tweet. 280 chars max.",\n  "teaser_set": { "teaser": "Post 1: hook only", "answer": "Post 2: the reveal, 3 lines max" },\n  "thread": ["1/3 ...", "2/3 ...", "3/3 ..."],\n  "note_promo": { "pattern_a": "Direct pitch", "pattern_b": "Subtle pitch", "pattern_c": "Trend-riding pitch" }\n}\n\n### Strict rules\n- Output NOTHING except the JSON object (no code blocks)\n- Write everything in English. Each line should be natural, conversational, anti-AI\n- teaser_set/answer, thread, and note_promo must also be under 280 chars\n- Thread is 3 posts (MVP). Start with 1/3, 2/3, 3/3\n- Teaser and standard tweet must use slightly different angles (no copy-paste)\n- note_promo should include concrete expressions that drive to a blog/article\n- Do not write thinking process outside of thought_log\n`;

      const systemPrompt = locale === 'ja'
        ? `あなたは戦略的なX投稿を設計するエキスパートです。7ステップの思考を行い、指定のJSONだけを返してください。\n- 280文字を超えない\n- 文体指示と制約を守る\n- AI特有の定型句を避け、文末をゆらがせる（Lvが高いほど崩す）\n- 絵文字・改行は読みやすさ優先で最小限\n`
        : `You are an expert at designing strategic X posts. Follow the 7-step thinking process and return ONLY the specified JSON.\n- Never exceed 280 characters\n- Follow the style instructions and constraints\n- Avoid AI-typical boilerplate, vary sentence endings (higher Lv = more natural variation)\n- Emojis and line breaks: minimal, prioritize readability\n`;

      const jsonText = await callAI(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        { temperature: 0.72, maxTokens: 2600, jsonMode: true }
      );

      const parsed = parseStrategyJSON(jsonText);
      const normalized = normalizeStrategyOutput(parsed);

      return {
        success: true,
        strategy: normalized,
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Unauthorized')) {
          throw new Error(getLocale() === 'ja' ? '認証トークンが無効です。セットアップをやり直してください。' : 'Invalid authentication token. Please redo setup.');
        }
        throw error;
      }
      throw new Error(getLocale() === 'ja' ? '戦略ツイート生成に失敗しました' : 'Strategy tweet generation failed');
    }
  },

  /**
   * 従来の自由形式プロンプト構築
   */
  buildFreeformPrompt(
    inputText: string,
    stylePrompt: string,
    fewShotExamples: string[],
    buzzInsights: string[],
    count: number = 20
  ): string {
    const locale = getLocale();

    if (locale === 'ja') {
      return this._buildFreeformPromptJa(inputText, stylePrompt, fewShotExamples, buzzInsights, count);
    }
    return this._buildFreeformPromptEn(inputText, stylePrompt, fewShotExamples, buzzInsights, count);
  },

  _buildFreeformPromptEn(
    inputText: string,
    stylePrompt: string,
    fewShotExamples: string[],
    buzzInsights: string[],
    count: number = 20
  ): string {
    const buzzInstructionsText = buzzInsights.length > 0
      ? '\n\n**Viral pattern instructions:**\n' + buzzInsights.map((insight, idx) => {
          const lower = insight.toLowerCase();
          if (lower.includes('question') || lower.includes('ask') || lower.includes('?')) {
            return `${idx + 1}. [Question] End with "right?", "anyone else?", "thoughts?" etc.`;
          }
          if (lower.includes('number') || lower.includes('specific') || lower.includes('stat')) {
            return `${idx + 1}. [Numbers] Use concrete numbers like "3 reasons", "5 minutes", "90% don't know"`;
          }
          if (lower.includes('emoji')) {
            return `${idx + 1}. [Emoji] Place emotional emojis sparingly in mid-sentence or at the end`;
          }
          if (lower.includes('line break') || lower.includes('list') || lower.includes('bullet')) {
            return `${idx + 1}. [Line breaks] Break long text into readable chunks (3-4 lines)`;
          }
          if (lower.includes('experience') || lower.includes('story') || lower.includes('example')) {
            return `${idx + 1}. [Story] Include personal experiences like "Yesterday I..." or "Just tried..."`;
          }
          if (lower.includes('short') || lower.includes('concise') || lower.includes('brief')) {
            return `${idx + 1}. [Short] Keep under 100 characters. Cut unnecessary words`;
          }
          if (lower.includes('long') || lower.includes('detail')) {
            return `${idx + 1}. [Long] 150-250 characters with context and reasoning`;
          }
          if (lower.includes('empathy') || lower.includes('relatable')) {
            return `${idx + 1}. [Relatable] Use "Anyone else?", "Tell me I'm not alone", "This hits different"`;
          }
          if (lower.includes('cta') || lower.includes('action') || lower.includes('call to')) {
            return `${idx + 1}. [CTA] Prompt action: "Try this", "Check it out", "Tell me yours"`;
          }
          return `${idx + 1}. ${insight}`;
        }).join('\n')
      : '';

    const fewShotSection = fewShotExamples.length > 0
      ? `\n\n**Reference examples (this user's actual tweets):**\n${fewShotExamples.map((t, i) => `${i + 1}. ${t}`).join("\n")}\n`
      : "";

    return `Based on the content below, generate ${count} natural, engaging tweet drafts in this user's writing style.

**Input content:**
${inputText}

**Style instructions (must follow strictly):**
${stylePrompt}
${fewShotSection}
${buzzInstructionsText}

**Internal thinking notes (do NOT output):**
- Prepare 5 hook types: surprise/question/conclusion/experience/contrast — rotate them
- Specifics: embed at least one proper noun, number, or concrete moment
- Rhythm: 0-2 line breaks, aim for 50-70 chars per line. 0-2 emojis only at emotional peaks
- Vary endings: shuffle question/empathy/conclusion/CTA so they never repeat consecutively
- Anti-AI: remove boilerplate, vary sentence endings ("right?", "honestly", "no cap", "idk" etc.)

**Generation rules:**
1. **280 characters max** — even 1 character over is a fail
2. **Perfectly replicate the style** — catchphrases, endings, emoji usage, punctuation must match
3. **Diverse variations** — ${count} different angles, structures, and tones (avoid repeating the same pattern)
4. **Human-first** — ruthlessly eliminate AI-sounding, ad-like, or boilerplate language. Keep it conversational
5. **Readability** — use line breaks for emphasis and pacing. Mix in some single-line tweets too
6. **Be specific (required)** — no abstract-only tweets. Include at least one:
   * Proper noun (VSCode, ChatGPT, YouTube, Starbucks, etc.)
   * Number (3 times, 5 minutes, 90%, 1 hour, etc.)
   * Concrete moment (the moment I opened it, just realized, today again, etc.)
7. **Inject emotion** — not dry explanations. Include feelings, opinions, lived experience
8. **Vary hooks** — never repeat the same closing phrase more than twice:
   * Questions: Anyone else? / Is it just me? / Thoughts? / Am I wrong? / Real ones know
   * Conclusions: Bottom line: / Lesson learned: / The move: / In short:
   * Empathy: This hits different / So true / Been there / Facts / Felt this
9. **Emphasis** — use emojis, quotation marks, or line breaks for contrast. Don't overdo it

**Banned patterns (never generate tweets containing these):**
- "I'd like to share..." / "Let me tell you about..." → sounds like an ad
- "What do you think?" repeated → generic engagement bait
- "Dear followers..." → too formal
- 5+ emojis → looks like spam
- "!!!" chains → too pushy
- Cliched phrases that scream AI → stale and predictable
- Same ending hook 3+ times → dead giveaway it's AI

**Output format (JSON):**
{
  "tweets": [
    "Tweet draft 1",
    "Tweet draft 2",
    ...(${count} total)
  ]
}`;
  },

  _buildFreeformPromptJa(
    inputText: string,
    stylePrompt: string,
    fewShotExamples: string[],
    buzzInsights: string[],
    count: number = 20
  ): string {
    const buzzInstructionsText = buzzInsights.length > 0
      ? '\n\n**バズパターンの活用指示:**\n' + buzzInsights.map((insight, idx) => {
          const lower = insight.toLowerCase();

          if (lower.includes('疑問形') || lower.includes('質問') || lower.includes('？')) {
            return `${idx + 1}. 【疑問形】最後に「〜ですよね？」「〜じゃない？」「〜かな？」などで締める`;
          }
          if (lower.includes('数字') || lower.includes('具体的') || lower.includes('統計')) {
            return `${idx + 1}. 【数字】「3つの理由」「5分で完了」「90%が知らない」など具体的な数値を使う`;
          }
          if (lower.includes('絵文字') || lower.includes('emoji')) {
            return `${idx + 1}. 【絵文字】感情を表す絵文字を文中・文末に適度に配置（やりすぎ注意）`;
          }
          if (lower.includes('改行') || lower.includes('箇条書き') || lower.includes('リスト')) {
            return `${idx + 1}. 【改行】長文は改行で区切り、読みやすくする（3-4行程度）`;
          }
          if (lower.includes('体験談') || lower.includes('実例') || lower.includes('エピソード')) {
            return `${idx + 1}. 【実例】「昨日〜した」「実際に〜してみた」など具体的な体験を入れる`;
          }
          if (lower.includes('短文') || lower.includes('簡潔')) {
            return `${idx + 1}. 【短文】100文字以内で簡潔に。余計な言葉を削る`;
          }
          if (lower.includes('長文') || lower.includes('詳しく')) {
            return `${idx + 1}. 【長文】150-250文字で詳しく説明。背景や理由も含める`;
          }
          if (lower.includes('共感') || lower.includes('あるある')) {
            return `${idx + 1}. 【共感】「わかる人いる？」「〜あるよね」など読者の共感を誘う`;
          }
          if (lower.includes('行動喚起') || lower.includes('cta') || lower.includes('アクション')) {
            return `${idx + 1}. 【行動喚起】「試してみて」「チェックして」「教えて」など行動を促す`;
          }

          return `${idx + 1}. ${insight}`;
        }).join('\n')
      : '';

    const fewShotSection = fewShotExamples.length > 0
      ? `\n\n**参考例（このユーザーの実際のツイート）:**\n${fewShotExamples.map((t, i) => `${i + 1}. ${t}`).join("\n")}\n`
      : "";

    return `以下の内容を基に、このユーザーの文体で、自然で魅力的なツイート案を${count}個生成してください。

**入力内容:**
${inputText}

**文体指示（厳守）:**
${stylePrompt}
${fewShotSection}
${buzzInstructionsText}

**内部思考メモ（出力しない）:**
- フック案を5種類用意：驚き/問い/結論/体験/対比などをローテーション
- 具体ワンポイント：固有名詞・数字・体験の瞬間から最低1つを埋め込む
- リズム設計：改行0〜2回・1行50〜70字目安。絵文字は0〜2個、感情の山だけに置く
- 末尾をばらけさせる：疑問/共感/結論/CTAをシャッフルして連続させない
- AI臭回避：定型句を削り、語尾をゆらがせる（「〜だよね」「〜かも」「〜ない？」等）

**生成ルール:**
1. **280文字以内（日本語）** - 1文字でもオーバーしたら失格
2. **文体指示を完璧に再現** - 口癖・語尾・絵文字・句読点の使い方まで一致させる
3. **多様なバリエーション** - 切り口・構成・トーンを${count}通りに変える（同じ型連打を避ける）
4. **人間らしさ重視** - AIっぽさ・広告っぽさ・定型文を徹底的に排除。口語でゆらぎを残す
5. **読みやすさ** - 改行は内容の区切り・強調に効果的に使う。改行しない案も混ぜる
6. **具体性（必須）** - 抽象だけ禁止。必ず1つ以上の具体を入れる：
   * 固有名詞（VSCode、AtCoder、布団、YouTube、スタバetc）
   * 数値（3回、5分、90%、1時間、10個etc）
   * 体験の瞬間（開いた瞬間、気づいたら、今日も、さっき、昨日etc）
7. **感情を込める** - 無機質な説明ではなく、感情・意見・体験を入れる
8. **フック多様化** - 同じ結びフレーズ（「わかる人いる？」等）を連続で使わない。
   * 疑問形：これ自分だけ？ / あるある？ / 同士いる？ / どう思う？ / ない？
   * 結論：結論： / 学び： / 今日の気づき： / 最適解： / つまり：
   * 共感：わかりみが深い / これな / あるよね / めっちゃわかる / それ
9. **強調の仕方** - 絵文字か「」カギ括弧、または改行でメリハリを付ける。過剰はしない

**NGパターン（これらを含むツイートは生成しない）:**
- 「〜についてご紹介します」「〜をお伝えします」→広告っぽい
- 「いかがでしたか？」→まとめサイト風
- 「皆さんは〜ですか？」→堅苦しい
- 絵文字の過剰使用（5個以上）→スパムっぽい
- 「！！！」の連続→押しつけがましい
- 「一番の贅沢」「痛感してる」等の定型フレーズ→既視感
- 同じ末尾フックの繰り返し（「わかる人いる？」を3回以上等）→AIっぽさ

**出力形式（JSON）:**
{
  "tweets": [
    "ツイート案1",
    "ツイート案2",
    ...（${count}個）
  ]
}`;
  },
};

function sanitizeJSONText(jsonText: string): string {
  return jsonText.trim().replace(/^```json\s*/i, '').replace(/```$/i, '').trim();
}

function parseStrategyJSON(jsonText: string): any {
  try {
    return JSON.parse(jsonText);
  } catch (error) {
    try {
      return JSON.parse(sanitizeJSONText(jsonText));
    } catch (err) {
      console.error('[generateService] JSON parse failed', err);
      throw new Error(getLocale() === 'ja' ? '生成結果の解析に失敗しました。もう一度お試しください。' : 'Failed to parse generated results. Please try again.');
    }
  }
}

function normalizeStrategyOutput(raw: any): StrategyGenerationResult {
  const teaserRaw = raw?.teaser_set || raw?.teaserSet || {};
  const noteRaw = raw?.note_promo || raw?.notePromo || {};
  const threadRaw = Array.isArray(raw?.thread) ? raw.thread : [];

  const thread = threadRaw
    .map((item: any) => {
      if (typeof item === 'string') return item;
      if (item && typeof item.text === 'string') return item.text;
      return '';
    })
    .filter(Boolean)
    .slice(0, 3);

  while (thread.length < 3) {
    thread.push('');
  }

  return {
    thoughtLog: (raw?.thought_log || raw?.thoughtLog || '').toString(),
    tweet: (raw?.tweet || '').toString(),
    teaserSet: {
      teaser: (teaserRaw.teaser || teaserRaw.t1 || '').toString(),
      answer: (teaserRaw.answer || teaserRaw.t2 || '').toString(),
    },
    thread,
    notePromo: {
      patternA: (noteRaw.pattern_a || noteRaw.patternA || noteRaw.a || '').toString(),
      patternB: (noteRaw.pattern_b || noteRaw.patternB || noteRaw.b || '').toString(),
      patternC: (noteRaw.pattern_c || noteRaw.patternC || noteRaw.c || '').toString(),
    },
  };
}
